import { FunctionComponent, useEffect, useState } from 'react';
import styles from './DaycareFastCheckIn.module.scss';
import Button from 'components/UI/V3/Button/Button';
import DaycareServiceSelector from 'components/CreateBooking/Daycare/UI/DaycareServiceSelector';
import { DaycareServiceWrapper } from 'model/Service';
import PetSelector from './UI/PetSelector';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { searchCustomersThunk } from 'actions/autocomplete/AutocompleteActions';
import { AutocompleteStatus } from 'reducers/autocomplete/AutocompleteState';
import { useDebouncedCallback } from 'use-debounce';
import { saveMultipleBookingThunk } from '@spike/new-bookings-action';
import { useApiClientWrapper, useAuth, useMarketplace, useTimeZone } from 'hooks';
import { NewBookingAppointment } from '@spike/new-booking-model';
import { NewBookingsStatus } from '@spike/new-bookings-action';
import { Week } from '@spike/model';
import moment, { Moment } from 'moment-timezone';

export const DaycareFastCheckIn: FunctionComponent<{ date: Moment }> = ({ date }) => {
    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();
    const auth = useAuth();
    const marketplace = useMarketplace();
    const timezone = useTimeZone();

    const { results, status } = useSelector((state: RootState) => state.autocomplete);
    const newBookingsStatus = useSelector((state: RootState) => state.newBookings.status);
    const [selectedPet, setSelectedPet] = useState<{ id: number; name: string; petId: number } | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedService, setSelectedService] = useState<DaycareServiceWrapper>();

    const handleSearchPets = useDebouncedCallback((query: string) => {
        if (query.trim()) dispatch(searchCustomersThunk(query));
    }, 250);

    const startDate = () => {
        const minutes = date.minutes();
        const roundedMinutes = Math.ceil(minutes / 15) * 15;
        return date.minutes(roundedMinutes).seconds(0).milliseconds(0);
    };

    const endDate = (date: Moment): Moment => {
        const dayKey = date.format('dddd').toLowerCase() as keyof Week;
        const businessHours = marketplace.schedule.week[dayKey];

        if (!businessHours || !businessHours.close) {
            return date.clone().add(15, 'minutes');
        }

        const actualMoment = moment.tz(date, timezone);
        const actualHHmm = Number(actualMoment.format('HHmm'));

        const [hours, minutes] = businessHours.close.split(':').map(Number);
        const closeHHmm = hours * 100 + minutes;

        if (actualHHmm >= closeHHmm) {
            return date.clone().add(15, 'minutes');
        }

        return date.clone().tz(timezone).hours(hours).minutes(minutes).seconds(0).milliseconds(0);
    };

    useEffect(() => {
        if (newBookingsStatus === NewBookingsStatus.SavingMultiple) {
            setIsLoading(true);
        } else {
            setIsLoading(false);
        }
    }, [newBookingsStatus]);

    const handleSaveDaycare = () => {
        const appointment: NewBookingAppointment[] = [
            {
                petId: selectedPet?.petId!,
                serviceId: selectedService?.serviceId!,
                date: startDate(),
                dateEnd: endDate(date),
                notes: '',
                createdByStaffId: auth.user!.staffId,
                clientId: selectedPet?.id!,
                status: 3
            }
        ];

        dispatch(saveMultipleBookingThunk(apiClientWrapper, appointment));
    };

    const pets =
        results
            ?.filter(r => r.type === 'Pet')
            .map(pet => {
                const match = pet.name.match(/^(.*?)\s*\((.*?)\)\s*$/);
                return {
                    id: pet.id ?? 0,
                    name: match?.[1]?.trim() ?? pet.name,
                    ownerName: match?.[2]?.trim() ?? '',
                    petType: pet.petType,
                    petId: pet.petId
                };
            }) ?? [];

    const loading = status === AutocompleteStatus.Searching;

    return (
        <div className={styles.container}>
            <div className={styles.body}>
                <div className={styles.section}>
                    <PetSelector
                        pets={pets}
                        loading={loading}
                        value={selectedPet?.petId}
                        onSearch={handleSearchPets}
                        onChange={pet => {
                            if (pet) {
                                setSelectedPet({ id: pet.id, name: pet.name, petId: pet.petId! });
                            } else {
                                setSelectedPet(null);
                            }
                            setSelectedService(undefined);
                        }}
                    />
                </div>

                {selectedPet && (
                    <div className={styles.sectionSelector}>
                        <DaycareServiceSelector
                            petId={selectedPet?.petId ?? 0}
                            selectedService={selectedService}
                            onSelectService={setSelectedService}
                        />
                    </div>
                )}
            </div>

            <div className={styles.footer}>
                <Button
                    label="Check In"
                    variant="green"
                    fullWidth
                    disabled={!selectedPet || !selectedService}
                    className={styles.button}
                    onClick={handleSaveDaycare}
                    isLoading={isLoading}
                />
            </div>
        </div>
    );
};

export default DaycareFastCheckIn;
